import React, { useState, useEffect, useMemo } from 'react';
import { API } from '@/App';
import axios from 'axios';

const REQUEST_TYPE_LABELS = {
  refabrication_identique: 'Refab. identique', refabrication_modifiee: 'Refab. modifiée',
  retouche: 'Retouche', casse: 'Casse', usure: 'Usure', inconfort: 'Inconfort',
  mauvaise_tenue: 'Mauvaise tenue', probleme_acoustique: 'Pb acoustique',
  defaut: 'Défaut', autre: 'Autre'
};

export default function SavStats() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/sav/stats/dashboard`)
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="p-8 text-center text-zinc-400">Chargement statistiques SAV...</div>;
  if (!stats) return <div className="p-8 text-center text-zinc-400">Impossible de charger les statistiques.</div>;

  const maxMotif = Math.max(...Object.values(stats.motifs || {}), 1);
  const maxProduit = Math.max(...Object.values(stats.produits || {}), 1);

  return (
    <div className="space-y-5" data-testid="sav-stats">
      {/* KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
        <KPI label="Total SAV" value={stats.total} />
        <KPI label="En cours" value={stats.open} color="text-amber-600" />
        <KPI label="Clôturés" value={stats.closed} color="text-emerald-600" />
        <KPI label="Taux SAV" value={`${stats.taux_sav}%`} sub={`sur ${stats.total_orders} cmd`} />
        <KPI label="Délai moyen" value={stats.avg_processing_days > 0 ? `${stats.avg_processing_days}j` : '—'} sub="traitement" />
        <KPI label="Délai livr. → SAV" value={stats.avg_livraison_sav_days > 0 ? `${stats.avg_livraison_sav_days}j` : '—'} />
      </div>

      {/* Répartition décisions */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Répartition des décisions</h3>
          <div className="space-y-3">
            <DecisionBar label="Gratuit" count={stats.repartition.gratuit} total={stats.total} color="bg-emerald-500" />
            <DecisionBar label="Payant" count={stats.repartition.payant} total={stats.total} color="bg-rose-500" />
            <DecisionBar label="Geste comm." count={stats.repartition.geste} total={stats.total} color="bg-blue-500" />
            <DecisionBar label="Sans décision" count={stats.repartition.sans_decision} total={stats.total} color="bg-zinc-300" />
          </div>
        </div>

        {/* Financier - Coût réel */}
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Cout reel de production</h3>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="bg-rose-50 border border-rose-200 p-3 rounded-sm">
                <p className="text-[10px] text-rose-500 uppercase tracking-wider">Cout total SAV</p>
                <p className="font-heading font-bold text-xl text-rose-700">{stats.cout_reel?.total?.toFixed(2) || '0.00'} €</p>
              </div>
              <div className="bg-zinc-50 border border-zinc-200 p-3 rounded-sm">
                <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Cout moyen / SAV</p>
                <p className="font-heading font-bold text-xl">{stats.cout_reel?.moyen?.toFixed(2) || '0.00'} €</p>
              </div>
            </div>

            <div className="space-y-2 pt-1">
              <CostBar label="Gratuit" cost={stats.cout_reel?.gratuit} count={stats.repartition.gratuit} color="bg-emerald-500" />
              <CostBar label="Payant" cost={stats.cout_reel?.payant} count={stats.repartition.payant} color="bg-rose-500" />
              <CostBar label="Geste comm." cost={stats.cout_reel?.geste} count={stats.repartition.geste} color="bg-blue-500" />
            </div>

            <div className="pt-2 border-t border-zinc-100">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div>
                  <p className="text-xs text-zinc-400">Embouts</p>
                  <p className="font-bold text-sm">{stats.cout_reel?.total_embouts || 0}</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-400">Taux h.</p>
                  <p className="font-bold text-sm">{stats.cout_reel?.taux_horaire || 13}€/h</p>
                </div>
                <div>
                  <p className="text-xs text-zinc-400">Matiere</p>
                  <p className="font-bold text-sm">{stats.cout_reel?.cout_matiere || 3}€/emb.</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* CA & indicateurs */}
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Chiffre d'affaires SAV</h3>
          <div className="space-y-4">
            <div>
              <p className="text-xs text-zinc-400 uppercase tracking-wider">CA SAV payant</p>
              <p className="font-heading font-bold text-2xl">{stats.financier.ca_payant.toFixed(2)} €</p>
            </div>
            <div>
              <p className="text-xs text-zinc-400 uppercase tracking-wider">Gestes commerciaux</p>
              <p className="font-heading font-bold text-2xl text-blue-600">{stats.financier.montant_gestes.toFixed(2)} €</p>
            </div>
            {stats.cout_reel?.total > 0 && (
              <div className="pt-2 border-t border-zinc-100">
                <p className="text-xs text-zinc-400 uppercase tracking-wider">Marge nette SAV payant</p>
                <p className={`font-heading font-bold text-lg ${(stats.financier.ca_payant - stats.cout_reel.payant) >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                  {(stats.financier.ca_payant - stats.cout_reel.payant).toFixed(2)} €
                </p>
              </div>
            )}
            <div className="flex gap-4 pt-2 border-t border-zinc-100">
              <div>
                <p className="text-xs text-zinc-400">Urgents</p>
                <p className="font-bold text-rose-600">{stats.urgent}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Identiques</p>
                <p className="font-bold">{stats.identique_vs_modifie.identique}</p>
              </div>
              <div>
                <p className="text-xs text-zinc-400">Modifies</p>
                <p className="font-bold">{stats.identique_vs_modifie.modifie}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Motifs */}
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Motifs fréquents</h3>
          <div className="space-y-2">
            {Object.entries(stats.motifs || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="text-xs text-zinc-600 w-28 truncate">{REQUEST_TYPE_LABELS[k] || k}</span>
                <div className="flex-1 h-4 bg-zinc-100 rounded-sm overflow-hidden">
                  <div className="h-full bg-zinc-700 rounded-sm" style={{ width: `${(v / maxMotif) * 100}%` }} />
                </div>
                <span className="text-xs font-bold w-6 text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Produits et clients */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Produits les plus concernés</h3>
          <div className="space-y-2">
            {Object.entries(stats.produits || {}).map(([k, v]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="text-xs text-zinc-600 flex-1 truncate">{k}</span>
                <div className="w-24 h-3 bg-zinc-100 rounded-sm overflow-hidden">
                  <div className="h-full bg-amber-500 rounded-sm" style={{ width: `${(v / maxProduit) * 100}%` }} />
                </div>
                <span className="text-xs font-bold w-6 text-right">{v}</span>
              </div>
            ))}
            {Object.keys(stats.produits || {}).length === 0 && <p className="text-xs text-zinc-400">Aucune donnée</p>}
          </div>
        </div>

        <div className="bg-white border border-zinc-200 p-4 rounded-sm">
          <h3 className="font-heading font-semibold text-sm mb-3 pb-2 border-b border-zinc-200">Clients les plus concernés</h3>
          <div className="space-y-2">
            {Object.entries(stats.clients || {}).map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm">
                <span className="text-zinc-600 truncate">{k}</span>
                <span className="font-bold">{v}</span>
              </div>
            ))}
            {Object.keys(stats.clients || {}).length === 0 && <p className="text-xs text-zinc-400">Aucune donnée</p>}
          </div>
        </div>
      </div>
    </div>
  );
}

function KPI({ label, value, color = '', sub = '' }) {
  return (
    <div className="bg-white border border-zinc-200 p-4 rounded-sm">
      <p className="text-xs text-zinc-400 uppercase tracking-wider">{label}</p>
      <p className={`font-heading font-bold text-2xl ${color}`}>{value}</p>
      {sub && <p className="text-xs text-zinc-400 mt-0.5">{sub}</p>}
    </div>
  );
}

function DecisionBar({ label, count, total, color }) {
  const pct = total > 0 ? Math.round((count / total) * 100) : 0;
  return (
    <div>
      <div className="flex justify-between text-xs mb-1">
        <span className="text-zinc-600">{label}</span>
        <span className="font-bold">{count} ({pct}%)</span>
      </div>
      <div className="h-3 bg-zinc-100 rounded-sm overflow-hidden">
        <div className={`h-full ${color} rounded-sm transition-all`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

function CostBar({ label, cost, count, color }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-2 h-8 rounded-sm ${color}`} />
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline">
          <span className="text-xs text-zinc-600">{label}</span>
          <span className="text-xs text-zinc-400">{count || 0} SAV</span>
        </div>
        <p className="font-heading font-bold text-sm">{(cost || 0).toFixed(2)} €</p>
      </div>
    </div>
  );
}
